import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Send, Paperclip } from 'lucide-react';
import { API, authHeaders, apiErr } from './adminApi';

/**
 * Reusable Send dialog for emailing a customer their estimate or invoice as a PDF attachment.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - kind: 'estimate' | 'invoice'
 *  - doc: { id, customer_name, customer_email, estimate_number?, invoice_number?, total }
 *  - onSent?: () => void   (called after a successful send so parent can reload)
 */
const SendDocumentDialog = ({ open, onClose, kind, doc, onSent }) => {
  const [recipient, setRecipient] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && doc) {
      setRecipient(doc.customer_email || '');
      setMessage('');
    }
  }, [open, doc]);

  if (!doc) return null;

  const label = kind === 'invoice' ? 'invoice' : 'estimate';
  const number = kind === 'invoice' ? doc.invoice_number : doc.estimate_number;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!recipient.trim() || !/.+@.+\..+/.test(recipient)) {
      toast.error('Enter a valid email address');
      return;
    }
    setSending(true);
    try {
      const url = `${API}/api/${kind === 'invoice' ? 'invoices' : 'estimates'}/${doc.id}/send`;
      const { data } = await axios.post(
        url,
        { recipient_email: recipient.trim(), message: message.trim() || null },
        { headers: authHeaders() },
      );
      const kb = Math.round((data?.attachment_bytes || 0) / 1024);
      toast.success(`Sent to ${recipient.trim()}${kb ? ` · PDF ${kb} KB` : ''}`);
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(`Send failed: ${apiErr(err)}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle data-testid="send-doc-title" className="flex items-center gap-2">
            <Send className="w-4 h-4 text-red-400" /> Email {label} to customer
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Sends a branded PDF of <span className="font-mono text-amber-300">{number || doc.id.slice(0, 8)}</span> via TayLord Arms email.
            You&apos;ll be BCC&apos;d automatically.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <Label className="text-gray-300 text-xs">Recipient email</Label>
            <Input
              data-testid="send-doc-recipient"
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              required
              placeholder="customer@example.com"
              className="bg-slate-800 border-slate-700 text-white"
            />
          </div>

          <div>
            <Label className="text-gray-300 text-xs">Personal message (optional)</Label>
            <Textarea
              data-testid="send-doc-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder={`Hi ${doc.customer_name || 'there'}, here's your ${label}…`}
              className="bg-slate-800 border-slate-700 text-white text-sm"
            />
            <p className="text-[11px] text-gray-500 mt-1">{message.length}/1000 characters · Shown as a highlighted note inside the email body.</p>
          </div>

          <div className="rounded border border-slate-700 bg-slate-950/40 p-3 text-xs text-gray-400 flex items-start gap-2">
            <Paperclip className="w-3 h-3 mt-0.5 text-amber-400" />
            <div>
              Attaches a PDF rendered with the chosen letterhead. Customer can reply directly to <span className="text-red-300">info.taylordarms@gmail.com</span>.
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
            <Button type="button" variant="outline" onClick={onClose} disabled={sending} className="border-slate-700 text-gray-300 hover:bg-slate-800">
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="send-doc-submit"
              disabled={sending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Send className="w-3 h-3 mr-2" />
              {sending ? 'Sending…' : `Send ${label}`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SendDocumentDialog;
